import {
  Disclosure,
  DisclosureContent,
  DisclosureProvider,
  Form,
  FormSubmit,
  Heading,
  HeadingLevel,
  useStoreState,
  type FormStore,
} from "@ariakit/react";
import { fold, MiddotSeparated, QRCode, useForm } from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import type { FeatureUnlock, SpaceGits } from "@indietabletop/types";
import { useCallback } from "react";
import { Link, useParams } from "wouter";
import { button } from "../../common/common.css.ts";
import { database, useQuery } from "../../database.ts";
import { Gang } from "../../engine/Gang.ts";
import { useCheckAccess } from "../CheckAccess/context.ts";
import { FailureView } from "../failures/FailureView.tsx";
import * as fmt from "../fmt.ts";
import { Divider } from "../forms/Divider.tsx";
import { SimpleFormCheckbox } from "../forms/FormTextField.tsx";
import { useGang } from "../GangPage/useGang.ts";
import { CloseIcon } from "../icons/CloseIcon.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import { RuleItemsSection } from "./RuleItemsSection.tsx";
import { page, preview, section, sidenav, tips } from "./style.css.ts";
import { Table } from "./Table.tsx";
import { useDismissOnEsc } from "./useDismissOnEsc.tsx";
import { useEnsureShareId } from "./useEnsureShareId.tsx";

function formatTypeAndRules(type: string, list: fmt.ListItem[]) {
  const typeRules = list.length > 0 ? fmt.list(list) : null;

  return typeRules ? `${type} (${typeRules})` : type;
}

function GitsSection(props: { gang: Gang }) {
  const { gits, faction } = props.gang;

  if (gits.length === 0) {
    return null;
  }

  return (
    <div className={section.container}>
      <Heading className={section.heading}>Gits</Heading>

      {faction?.unpack(
        (f) => {
          return (
            <p className={section.factionInfo}>
              <strong>{f.name}:</strong> All gits have the{" "}
              <strong>{fmt.list(f.rules)}</strong> rule.
            </p>
          );
        },
        () => null,
        () => null,
      )}
      <Table
        columns={[
          {
            key: "name",
            label: "Name",
            padding: true,
          },
          {
            key: "scoot",
            label: "Scoot",
            align: "center",
            inlineSize: "12%",
          },
          {
            key: "headbutt",
            label: "Headbutt",
            align: "center",
            inlineSize: "12%",
          },
          {
            key: "toughness",
            label: "Toughness",
            align: "center",
            inlineSize: "12%",
          },
          {
            key: "attitude",
            label: "Attitude",
            align: "center",
            inlineSize: "12%",
          },
        ]}
        data={gits.map((git) => {
          return {
            name: (
              <>
                <div className={section.rowHead}>{git.name}</div>

                <MiddotSeparated className={section.rowHeadSummary}>
                  {formatTypeAndRules(git.type.name, git.typeRules)}
                  {git.loadoutSummary}
                </MiddotSeparated>
              </>
            ),
            scoot: git.scoot,
            headbutt: git.headbutt,
            toughness: git.toughness,
            attitude: git.attitude,
          };
        })}
      />
    </div>
  );
}

function VehicleSection(props: { gang: Gang }) {
  const { vehicles } = props.gang;

  if (vehicles.length === 0) {
    return null;
  }

  return (
    <div className={section.container}>
      <Heading className={section.heading}>Vehicles</Heading>

      <Table
        columns={[
          { key: "name", label: "Name", padding: true },
          {
            key: "engine",
            label: "Engine",
            align: "center",
            inlineSize: "12%",
          },
          {
            key: "seats",
            label: "Seats",
            align: "center",
            inlineSize: "12%",
          },
          {
            key: "toughness",
            label: "Toughness",
            align: "center",
            inlineSize: "12%",
          },
        ]}
        data={vehicles.map((vehicle) => {
          return {
            name: (
              <>
                <div className={section.rowHead}>{vehicle.name}</div>

                <MiddotSeparated className={section.rowHeadSummary}>
                  {formatTypeAndRules(vehicle.type.name, vehicle.rules)}
                  {vehicle.loadoutSummary}
                </MiddotSeparated>
              </>
            ),
            engine: vehicle.engine,
            seats: vehicle.seats,
            toughness: vehicle.toughness,
          };
        })}
      />
    </div>
  );
}

function WeaponsSection(props: { gang: Gang; form: FormStore<PrintOptions> }) {
  const { gang } = props;
  const { uniqueWeapons } = gang;

  if (uniqueWeapons.length === 0) {
    return null;
  }

  return (
    <div className={section.container}>
      <Heading className={section.heading}>Weapons</Heading>

      <Table
        columns={[
          {
            key: "name",
            label: "Name",
            padding: true,
          },
          {
            key: "range",
            label: "Range",
            align: "center",
            inlineSize: "12%",
          },
          {
            key: "dice",
            label: "Dice",
            align: "center",
            inlineSize: "12%",
          },
          {
            key: "damage",
            label: "Damage",
            align: "center",
            inlineSize: "12%",
          },
        ]}
        data={uniqueWeapons
          .slice()
          .sort((left, right) => left.compare(right))
          .map((weapon) => {
            return {
              name: (
                <>
                  <div className={section.rowHead}>{weapon.name}</div>
                  <div className={section.rowHeadSummary}>
                    {fmt.list(weapon.rules)}
                  </div>
                </>
              ),
              range: weapon.range,
              dice: weapon.dice,
              damage: weapon.damage,
            };
          })}
      />
    </div>
  );
}

function WeaponRulesSection(props: {
  gang: Gang;
  form: FormStore<PrintOptions>;
}) {
  const { form, gang } = props;
  const { weaponSpecialRules } = gang;

  const includeWeapons = useStoreState(
    form,
    (state) => state.values.printWeaponRules,
  );

  if (weaponSpecialRules.length === 0 || !includeWeapons) {
    return null;
  }

  return (
    <RuleItemsSection
      items={weaponSpecialRules
        .slice()
        .sort((left, right) => left.name.localeCompare(right.name))}
    />
  );
}

function GitRulesSection(props: {
  gang: Gang;
  form: FormStore<PrintOptions>;
  unlocks: FeatureUnlock[];
}) {
  const { gang, form, unlocks } = props;
  const { gitRules, uniqueGear, uniqueTreatments, factionRules } = gang;
  const printSection = useStoreState(form, (s) => s.values.printGitRules);

  if (!printSection) {
    return null;
  }

  const hasAccess = unlocks.some((u) => u.feature === "SPACE_GITS_CORE_RULES");
  const allItems = [
    ...factionRules,
    ...gitRules,
    ...uniqueGear,
    ...uniqueTreatments,
  ];
  const items = hasAccess ? allItems : [...gitRules];
  const hiddenItemsCount = allItems.length - items.length;

  return (
    <RuleItemsSection
      hiddenItemsCount={hiddenItemsCount}
      items={items.sort((left, right) => left.name.localeCompare(right.name))}
    />
  );
}

function VehicleRulesSection(props: {
  gang: Gang;
  form: FormStore<PrintOptions>;
  unlocks: FeatureUnlock[];
}) {
  const { gang, form, unlocks } = props;
  const { uniqueMods, uniqueRepairs, uniqueVehicleRules } = gang;
  const printSection = useStoreState(form, (s) => s.values.printVehicleRules);

  if (!printSection) {
    return null;
  }

  const hasAccess = unlocks.some((u) => u.feature === "SPACE_GITS_CORE_RULES");
  const allItems = [...uniqueMods, ...uniqueRepairs, ...uniqueVehicleRules];
  const items = hasAccess ? allItems : [];
  const hiddenItemsCount = allItems.length - items.length;

  return (
    <RuleItemsSection
      hiddenItemsCount={hiddenItemsCount}
      items={items.sort((left, right) => left.name.localeCompare(right.name))}
    />
  );
}

type PrintOptions = {
  printWeaponRules: boolean;
  printGitRules: boolean;
  printVehicleRules: boolean;
};

function PrintSidebar(props: { gang: Gang; form: FormStore<PrintOptions> }) {
  const backHref = `~/gangs/g/${props.gang.id}`;
  useDismissOnEsc(backHref);

  return (
    <div className={sidenav.container}>
      <div className={sidenav.header}>
        <div className={sidenav.heading}>Print</div>

        <Link href={backHref}>
          <CloseIcon />
        </Link>
      </div>
      <Divider />

      <div className={sidenav.content}>
        <DisclosureProvider>
          <section>
            <p>
              To get the best results when printing, check out the{" "}
              <Disclosure className={interactiveText}>
                printing tips & tricks
              </Disclosure>
              .
            </p>
          </section>

          <DisclosureContent>
            <h2 className={sidenav.subheading}>Tips & Tricks</h2>

            <p className={tips.text}>
              <strong>Use Chrome, Edge, or Firefox browser.</strong> Safari does
              not reproduce the page as it was designed.
            </p>

            <p className={tips.text}>
              <strong>Prefer printing on desktop.</strong> It is possible to use
              a phone/tablet, but the output is usually worse.
            </p>

            <p className={tips.text}>
              <strong>Use system dialog to fine-tune your print.</strong> You
              should be able to change <em>font size</em> with scale, set{" "}
              <em>margins</em>, and switch off <em>headers</em>.
            </p>

            <p className={tips.text}>
              <strong>Use Print to PDF</strong> to get a digital version of your
              printout.
            </p>
          </DisclosureContent>
        </DisclosureProvider>

        <section>
          <h2 className={sidenav.subheading}>Sections</h2>
          <p>Which sections do you want to print?</p>

          <div className={sidenav.checkboxes}>
            <SimpleFormCheckbox
              name={props.form.names.printGitRules}
              label="Git rules (gear, treatments, special)"
            />
            <SimpleFormCheckbox
              name={props.form.names.printVehicleRules}
              label="Vehicle rules (mods, repairs, special)"
            />
            <SimpleFormCheckbox
              name={props.form.names.printWeaponRules}
              label="Weapon rules"
            />
          </div>
        </section>

        <div className={sidenav.printButtonContainer}>
          <FormSubmit className={button({ mask: 1, maxInlineSize: "auto" })}>
            Print
          </FormSubmit>
        </div>
      </div>
    </div>
  );
}

function GangPrintPageSuccessView(props: {
  gang: Gang;
  settings: SpaceGits.PrintSettings;
  unlocks: FeatureUnlock[];
}) {
  const { gang, settings, unlocks } = props;

  const { form } = useForm({
    defaultValues: settings,

    async onSubmit({ values }) {
      return await database.settings.upsertOne(values);
    },

    onSuccess() {
      window.print();
    },
  });

  const shareLink = useEnsureShareId(gang).mapSuccess((snapshotId) => {
    return Gang.getSnapshotLink(window.origin, snapshotId);
  });

  return (
    <Form store={form} resetOnSubmit={false}>
      <HeadingLevel>
        <div className={page.container}>
          <div className={preview.container}>
            <div className={preview.header}>
              <div>
                <Heading className={preview.title}>{gang.name}</Heading>
                <MiddotSeparated className={preview.summary}>
                  {`Reputation: ${gang.reputation}`}
                  {gang.faction?.valueOrNull()?.name}
                </MiddotSeparated>
              </div>

              <div>
                <QRCode url={shareLink} />
                <div className={preview.qrCodeText}>
                  Scan to load in <br />
                  Space Gits app
                </div>
              </div>
            </div>

            <HeadingLevel>
              <GitsSection gang={gang} />
              <GitRulesSection gang={gang} form={form} unlocks={unlocks} />
              <VehicleSection gang={gang} />
              <VehicleRulesSection gang={gang} form={form} unlocks={unlocks} />
              <WeaponsSection gang={gang} form={form} />
              <WeaponRulesSection gang={gang} form={form} />
            </HeadingLevel>
          </div>

          <PrintSidebar gang={gang} form={form} />
        </div>
      </HeadingLevel>
    </Form>
  );
}

function usePrintSettings() {
  return useQuery(
    useCallback(async (db) => await db.settings.getPrintSettings(), []),
    `print_settings`,
  );
}

export function GangPrintPage() {
  const { gangId } = useParams<{ gangId: string }>();
  const gangResult = useGang(gangId);
  const settingsResult = usePrintSettings();
  const [accessResult] = useCheckAccess({ performFetch: true });
  const result = fold([gangResult, settingsResult]);

  if (result.isPending || accessResult.isPending) {
    return <LoadingView />;
  }

  if (result.isFailure) {
    return <FailureView failure={result.failure} />;
  }

  const [gang, settings] = result.value;
  return (
    <GangPrintPageSuccessView
      gang={gang}
      settings={settings}
      unlocks={accessResult.valueOrNull()?.unlocks ?? []}
    />
  );
}
